import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import PhotoGallery from "@/components/base/PhotoGallery";
import type { ExperienceDetail } from "@/mocks/experienceDetails";

export default function ExperienceOverview({ detail }: { detail: ExperienceDetail }) {
  return (
    <>
      <section id="overview" className="sun-glaze bg-background-50 py-20 md:py-28">
        <div className="w-full px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <Reveal className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-5">
                <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
                <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
                  The Experience
                </span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold">
                What to Expect
              </h2>
              <div className="mt-6 space-y-5">
                {detail.overview.map((paragraph) => (
                  <p key={paragraph} className="text-base md:text-lg text-foreground-700 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </Reveal>

            <Reveal className="lg:col-span-5" delay={120}>
              <div className="bg-primary-900 text-background-100 rounded-lg p-8">
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500 text-background-50">
                  <i className="ri-price-tag-3-line text-2xl"></i>
                </span>
                <p className="text-xs font-semibold uppercase tracking-wider text-accent-300 mt-6">
                  Starting from
                </p>
                <p className="font-heading text-3xl md:text-4xl text-background-50 font-semibold mt-2">
                  {detail.priceFrom}
                </p>
                <p className="text-sm text-background-200 leading-relaxed mt-4">
                  Final pricing depends on group size, season and the activities you choose. Tell
                  us your plan and we will send a clear quote.
                </p>
                <a
                  href="#reserve"
                  className="mt-7 inline-flex w-full items-center justify-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-6 py-4 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calendar-check-line text-lg"></i>
                  Reserve Your Date
                </a>
                <a
                  href="#enquire"
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 bg-background-50/10 text-background-50 border border-background-50/30 text-sm font-semibold px-6 py-4 rounded-md hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Enquire About This Experience
                  <i className="ri-arrow-right-line"></i>
                </a>
                <p className="text-xs text-background-300 mt-4 text-center">
                  No payment now • We reply within 24 hours
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="sun-glaze bg-background-100 py-20 md:py-28">
        <div className="w-full px-4 md:px-8">
          <Reveal className="max-w-3xl mb-10 md:mb-14">
            <div className="flex items-center gap-3 mb-5">
              <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
              <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
                Photo Gallery
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold">
              Moments From This Experience
            </h2>
            <p className="mt-4 text-base text-foreground-600 leading-relaxed">
              A glimpse of the fields, food and people you will meet. Tap any photo to view it
              larger.
            </p>
          </Reveal>

          <Reveal delay={100}>
            <PhotoGallery images={detail.gallery} />
          </Reveal>
        </div>
      </section>
    </>
  );
}